import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { ArrowLeft, CreditCard, Truck, Building2, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const { cart, getTotalPrice, clearCart } = useCart();
  const navigate = useNavigate();
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    county: '',
    postalCode: '',
    paymentMethod: 'card'
  });

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.phone || !formData.address || !formData.city) {
      toast.error('Te rugăm să completezi toate câmpurile obligatorii');
      return;
    }

    setLoading(true);

    try {
      // Prepare order data
      const orderData = {
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        shipping_address: formData.address,
        shipping_city: formData.city,
        shipping_county: formData.county || null,
        shipping_postal_code: formData.postalCode || null,
        payment_method: formData.paymentMethod,
        items: cart.map(item => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          unit_price: item.price,
          total_price: item.price * item.quantity,
          image_url: item.image_url || null
        })),
        subtotal: getTotalPrice(),
        shipping_cost: 0,
        total: getTotalPrice()
      };

      // Send order to backend
      const response = await axios.post(`${API}/orders`, orderData);
      
      if (response.data.success) {
        setOrderNumber(response.data.order_number);
        setOrderPlaced(true);
        clearCart();
        toast.success('Comanda a fost plasată cu succes!');
      }
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error('Eroare la plasarea comenzii. Te rugăm să încerci din nou.');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0 && !orderPlaced) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50">
        <Navbar />
        <div className="pt-24 pb-12 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="glass-card p-12">
              <h1 className="text-3xl font-bold text-slate-900 mb-4">
                Nu ai produse în coș
              </h1>
              <Link to="/magazin">
                <Button className="btn-primary">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Mergi la Magazin
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50">
        <Navbar />
        <div className="pt-24 pb-12 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-card p-12"
              data-testid="order-success"
            >
              <CheckCircle2 className="w-24 h-24 text-green-500 mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Comanda a fost plasată!
              </h1>
              {orderNumber && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 inline-block">
                  <p className="text-sm text-green-700">Număr comandă:</p>
                  <p className="text-2xl font-bold text-green-800 font-mono">{orderNumber}</p>
                </div>
              )}
              <p className="text-slate-600 mb-8">
                Îți mulțumim pentru comandă! Vei primi un email de confirmare în curând.
                <br />
                Un reprezentant IzoMAG te va contacta pentru finalizarea detaliilor.
              </p>
              <div className="flex justify-center gap-4">
                <Link to="/">
                  <Button variant="outline">
                    Pagina Principală
                  </Button>
                </Link>
                <Link to="/magazin">
                  <Button className="btn-primary">
                    Continuă Cumpărăturile
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50" data-testid="checkout-page">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Link to="/cos" className="text-cyan-600 hover:text-cyan-700 flex items-center gap-2 mb-4">
              <ArrowLeft className="w-4 h-4" /> Înapoi la Coș
            </Link>
            <h1 className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="checkout-title">
              Finalizare Comandă
            </h1>
          </motion.div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Form Fields */}
              <div className="lg:col-span-2 space-y-6">
                {/* Contact Info */}
                <Card className="glass-card p-6">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-cyan-500" />
                    Informații Contact
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Nume Complet *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Ion Popescu"
                        className="mt-1"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="email@exemplu.ro"
                        className="mt-1"
                        data-testid="input-email"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="phone">Telefon *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="0722 123 456"
                        className="mt-1"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                </Card>

                {/* Shipping Address */}
                <Card className="glass-card p-6">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Truck className="w-5 h-5 text-cyan-500" />
                    Adresa de Livrare
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor="address">Adresa *</Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Strada, Număr, Bloc, Scara, Apartament"
                        className="mt-1"
                        data-testid="input-address"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">Oraș *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="București"
                        className="mt-1"
                        data-testid="input-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="county">Județ</Label>
                      <Input
                        id="county"
                        name="county"
                        value={formData.county}
                        onChange={handleInputChange}
                        placeholder="Ilfov"
                        className="mt-1"
                        data-testid="input-county"
                      />
                    </div>
                    <div>
                      <Label htmlFor="postalCode">Cod Poștal</Label>
                      <Input
                        id="postalCode"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        placeholder="012345"
                        className="mt-1"
                        data-testid="input-postal"
                      />
                    </div>
                  </div>
                </Card>

                {/* Payment Method */}
                <Card className="glass-card p-6">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-cyan-500" />
                    Metoda de Plată
                  </h2>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-slate-50 transition">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="card"
                        checked={formData.paymentMethod === 'card'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-cyan-500"
                        data-testid="payment-card"
                      />
                      <CreditCard className="w-5 h-5 text-slate-600" />
                      <div>
                        <p className="font-medium text-slate-900">Card Bancar</p>
                        <p className="text-sm text-slate-500">Plată online securizată</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-slate-50 transition">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="transfer"
                        checked={formData.paymentMethod === 'transfer'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-cyan-500"
                        data-testid="payment-transfer"
                      />
                      <Building2 className="w-5 h-5 text-slate-600" />
                      <div>
                        <p className="font-medium text-slate-900">Transfer Bancar</p>
                        <p className="text-sm text-slate-500">Plată în avans prin transfer</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-slate-50 transition">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="cash"
                        checked={formData.paymentMethod === 'cash'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-cyan-500"
                        data-testid="payment-cash"
                      />
                      <Truck className="w-5 h-5 text-slate-600" />
                      <div>
                        <p className="font-medium text-slate-900">Ramburs la Livrare</p>
                        <p className="text-sm text-slate-500">Plătești când primești comanda</p>
                      </div>
                    </label>
                  </div>
                </Card>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <Card className="glass-card p-6 sticky top-24">
                  <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Sumar Comandă
                  </h2>
                  
                  <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
                    {cart.map(item => (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span className="text-slate-600 truncate flex-1 mr-2">
                          {item.name} x{item.quantity}
                        </span>
                        <span className="font-medium">{(item.price * item.quantity).toFixed(2)} LEI</span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-slate-200 pt-4 space-y-2">
                    <div className="flex justify-between text-slate-600">
                      <span>Subtotal</span>
                      <span>{getTotalPrice().toFixed(2)} LEI</span>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>Transport</span>
                      <span className="text-green-600">Se va calcula</span>
                    </div>
                    <div className="flex justify-between text-xl font-bold pt-2 border-t">
                      <span>Total</span>
                      <span className="text-orange-500">{getTotalPrice().toFixed(2)} LEI</span>
                    </div>
                    <p className="text-xs text-slate-500">Prețurile includ TVA</p>
                  </div>

                  <Button
                    type="submit"
                    className="btn-primary w-full mt-6"
                    data-testid="place-order-btn"
                    disabled={loading}
                  >
                    {loading ? 'Se procesează...' : 'Plasează Comanda'}
                  </Button>

                  <p className="text-xs text-slate-500 text-center mt-4">
                    Prin plasarea comenzii, ești de acord cu termenii și condițiile noastre.
                  </p>
                </Card>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
